import React, { useMemo, useState } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, Input, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { computeLocationMonthlyMetrics } from './locationMetricsCore';
import type {
  MetricMode,
  PicqerLocation,
  PicqerLocationStockHistoryEntry,
  PicqerWarehouse,
} from './types';

const { Text, Paragraph } = Typography;

interface WarehouseLocationExplorerProps {
  warehouse: PicqerWarehouse;
  locations: PicqerLocation[];
  historyByLocation: Record<number, PicqerLocationStockHistoryEntry[]>;
  preRangeStockByLocation: Record<number, number>;
  monthKeys: string[];
  metricMode: MetricMode;
  onClose: () => void;
}

interface LocationRow {
  key: number;
  idlocation: number;
  name: string;
  historyCount: number;
  peakMonth: string;
  peakPct: number;
  maxUsed: boolean;
}

export const WarehouseLocationExplorer: React.FC<WarehouseLocationExplorerProps> = ({
  warehouse,
  locations,
  historyByLocation,
  preRangeStockByLocation,
  monthKeys,
  metricMode,
}) => {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const rows: LocationRow[] = useMemo(() => {
    return locations
      .filter((l) => historyByLocation[l.idlocation] !== undefined)
      .map((loc) => {
        const history = historyByLocation[loc.idlocation] ?? [];
        const pre = preRangeStockByLocation[loc.idlocation] ?? 0;
        const months = computeLocationMonthlyMetrics(history, pre, monthKeys, metricMode);
        const peak = months.reduce(
          (best, m) => (m.fraction > best.fraction ? m : best),
          months[0] ?? { month: '—', fraction: 0, used: false },
        );
        return {
          key: loc.idlocation,
          idlocation: loc.idlocation,
          name: loc.name,
          historyCount: history.length,
          peakMonth: peak.month,
          peakPct: Math.round(peak.fraction * 1000) / 10,
          maxUsed: peak.used,
        };
      });
  }, [locations, historyByLocation, preRangeStockByLocation, monthKeys, metricMode]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) => r.name.toLowerCase().includes(q) || String(r.idlocation).includes(q),
    );
  }, [rows, search]);

  const selectedLoc = selectedId != null ? locations.find((l) => l.idlocation === selectedId) : null;
  const selectedHistory = selectedId != null ? historyByLocation[selectedId] ?? [] : [];
  const selectedPre = selectedId != null ? preRangeStockByLocation[selectedId] ?? 0 : 0;

  const locationChartData = useMemo(() => {
    if (selectedId == null) return [];
    const months = computeLocationMonthlyMetrics(selectedHistory, selectedPre, monthKeys, metricMode);
    return months.map((m) => ({
      month: m.month,
      pct: Math.round(m.fraction * 1000) / 10,
      used: m.used ? 1 : 0,
    }));
  }, [selectedId, selectedHistory, selectedPre, monthKeys, metricMode]);

  const columns: ColumnsType<LocationRow> = [
    { title: 'Location', dataIndex: 'name', key: 'name', ellipsis: true },
    { title: 'ID', dataIndex: 'idlocation', key: 'idlocation', width: 90 },
    { title: 'History events', dataIndex: 'historyCount', key: 'historyCount', width: 110 },
    {
      title: 'Peak month %',
      dataIndex: 'peakPct',
      key: 'peakPct',
      width: 110,
      render: (v: number) => `${v.toFixed(1)}%`,
    },
    { title: 'Peak month', dataIndex: 'peakMonth', key: 'peakMonth', width: 100 },
  ];

  return (
    <Card
      title={`Locations — ${warehouse.name} (${warehouse.idwarehouse})`}
      size="small"
      style={{ marginBottom: 24 }}
    >
      <Paragraph type="secondary" style={{ marginBottom: 12 }}>
        {rows.length} locations with loaded history (of {locations.length} total). Click a row to inspect
        occupation over the selected month range and raw Picqer history JSON.
      </Paragraph>
      <Input.Search
        placeholder="Search by name or ID"
        allowClear
        style={{ maxWidth: 320, marginBottom: 12 }}
        onChange={(e) => setSearch(e.target.value)}
      />
      <Table
        rowKey="idlocation"
        size="small"
        columns={columns}
        dataSource={filtered}
        pagination={{ pageSize: 25, showSizeChanger: true, pageSizeOptions: ['25', '50', '100'] }}
        onRow={(record) => ({
          onClick: () => setSelectedId(record.idlocation),
          style: {
            cursor: 'pointer',
            background: selectedId === record.idlocation ? '#e6f4ff' : undefined,
          },
        })}
      />

      {selectedLoc && (
        <div style={{ marginTop: 16 }}>
          <Text strong>
            {selectedLoc.name} (#{selectedLoc.idlocation})
          </Text>
          <div style={{ width: '100%', height: 220, marginTop: 8, marginBottom: 12 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={locationChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" angle={-45} textAnchor="end" height={60} tick={{ fontSize: 10 }} />
                <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} width={40} />
                <Tooltip formatter={(v: number) => [`${v}%`, 'Occupied']} />
                <Line type="monotone" dataKey="pct" stroke="#1677ff" dot={false} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <Table
            size="small"
            pagination={false}
            style={{ marginBottom: 12, maxWidth: 360 }}
            columns={[
              { title: 'Month', dataIndex: 'month', key: 'month' },
              {
                title: 'Occupied',
                dataIndex: 'pct',
                key: 'pct',
                render: (v: number) => `${v.toFixed(1)}%`,
              },
              {
                title: 'Any stock',
                dataIndex: 'used',
                key: 'used',
                render: (v: number) => (v ? 'Yes' : 'No'),
              },
            ]}
            dataSource={locationChartData.map((r) => ({ ...r, key: r.month }))}
          />
          <Text type="secondary">Raw stock history ({selectedHistory.length} events)</Text>
          <pre
            style={{
              maxHeight: 280,
              overflow: 'auto',
              background: '#fafafa',
              border: '1px solid #eee',
              padding: 12,
              fontSize: 11,
              marginTop: 8,
            }}
          >
            {JSON.stringify(
              {
                idlocation: selectedLoc.idlocation,
                name: selectedLoc.name,
                preRangeStock: selectedPre,
                history: selectedHistory,
              },
              null,
              2,
            )}
          </pre>
        </div>
      )}
    </Card>
  );
};
